import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

// Short, self-dismissing notices in the corner: "Сохранено" after a successful
// save, and the reason when an action fails and there is no form of its own to
// show it in. Mounted once at the app root (main.tsx); `useEntityAction` raises
// them, so individual buttons don't have to.

type ToastKind = "success" | "error";

interface ToastItem {
  id: number;
  kind: ToastKind;
  text: string;
}

interface ToastApi {
  success: (text: string) => void;
  error: (text: string) => void;
}

const SUCCESS_MS = 2500;
const ERROR_MS = 7000;
const MAX_VISIBLE = 4;

// Outside a provider (a unit test, an isolated component) toasts are a no-op.
const NOOP: ToastApi = { success: () => {}, error: () => {} };

const ToastContext = createContext<ToastApi>(NOOP);

export function useToast(): ToastApi {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, text: string) => {
      const id = nextId.current++;
      setItems((prev) => [...prev.slice(-(MAX_VISIBLE - 1)), { id, kind, text }]);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), kind === "error" ? ERROR_MS : SUCCESS_MS),
      );
    },
    [dismiss],
  );

  useEffect(() => {
    const live = timers.current;
    return () => {
      for (const timer of live.values()) clearTimeout(timer);
      live.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (text) => push("success", text),
      error: (text) => push("error", text),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-2"
        data-testid="toasts"
      >
        {items.map((t) => (
          <button
            key={t.id}
            type="button"
            role={t.kind === "error" ? "alert" : "status"}
            onClick={() => dismiss(t.id)}
            className={
              "pointer-events-auto rounded-md border px-3 py-2 text-left text-sm shadow-lg " +
              (t.kind === "error"
                ? "border-red-500/50 bg-red-50 text-red-800"
                : "border-emerald-500/50 bg-emerald-50 text-emerald-800")
            }
          >
            {t.kind === "success" ? "✓ " : "⚠ "}
            {t.text}
          </button>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
